import { base64 } from '@primer-io/sdk-utils/base64';
import { delay } from '@primer-io/sdk-utils/delay';
import { derToRaw } from '@primer-io/sdk-utils/ecdsa';
import { PrimerJSLogger } from '@primer-io/sdk-utils/PrimerJSLogger';
import { loadScriptWithSRI } from './utils/loadScriptWithSRI';
import { fetchAndVerify } from './utils/fetchAndVerify';
import { VerificationError } from './utils/VerificationError';
import {
  SerializedJSON,
  StateProcessor,
  StateProcessorFactory,
  StateProcessorConfig,
  SDKContext,
} from './types';

// ---------------------------------------------------------------------------
// Manifest types
// ---------------------------------------------------------------------------

/** A script, plus pre-compressed copies of it */
interface ScriptAsset {
  /** URL of the script */
  url: string;
  /** SHA256 hash of the script; the compressed copies decompress to the same bytes */
  sha256: string;
  /** GZ-compressed copy of the script */
  gz: string;
  /** Brotli-compressed copy of the script */
  br: string;
}

interface CelTarget {
  /** details for a lightweight JS wrapper for the CEL WASM binary */
  js: ScriptAsset;
  /** details for the raw WASM binary */
  wasm: {
    /** URL of the raw WASM binary */
    url: string;
    /** SHA256 hash of the raw WASM binary */
    sha256: string;
    /** GZ-compressed version of the raw WASM binary (if the client supports it) */
    gz: string;
    /** Brotli-compressed version of the raw WASM binary (if the client supports it) */
    br: string;
  };
}

interface StateProcessorManifest {
  /** ISO timestamp when the manifest was generated */
  generatedAt: string;
  /**
   * metadata about the versions of the state processor library that can be
   * loaded and their integrity details
   */
  stateProcessor: {
    version: string;
    /** UMD version of the library and its SHA256, to be used by mobile SDKs */
    umd: ScriptAsset;
    /** ESM version of the library and its SHA256, to be used by web SDK */
    esm: ScriptAsset;
  };
  /** metadata about the CEL execution library binary */
  cel: {
    version: string;
    /** ESM target — JS glue loaded via dynamic import(), used by the web SDK */
    web: CelTarget;
    /** noModules (IIFE) target — JS glue loaded via <script> tag, used by mobile SDKs */
    noModules: CelTarget;
  };
}

// ---------------------------------------------------------------------------
// Globals
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    // Set by the CEL noModules artifact on load. The build emits
    // `globalThis.primer_wasm_bindgen = …` (a renamed wasm-bindgen export).
    // Calling `primer_wasm_bindgen(wasmBytes)` initializes the WASM; the
    // `evaluate` method is the public surface used by the state processor.
    primer_wasm_bindgen: ((input: ArrayBuffer) => Promise<unknown>) & {
      initSync: (input: WebAssembly.Module | ArrayBuffer) => unknown;
      evaluate: (expr: string, jsonData: string) => string;
    };
    // Set by the state processor UMD bundle on load (see manifest.stateProcessor.umd).
    StateProcessor: StateProcessorFactory;
  }
}

// ---------------------------------------------------------------------------
// Manifest signature verification
// ---------------------------------------------------------------------------

interface SignedManifestResponse {
  signature: string;
  manifest: string;
}

/**
 * ECDSA P-256 public keys (base64-encoded DER SPKI) used to verify the manifest signature.
 * Two keys are provided to support seamless key rotation — verification succeeds if
 * either key validates the signature.
 *
 * Order is significant and must be kept in sync with the SP key registry:
 *   [0] SP_KEY_1
 *   [1] SP_KEY_2
 */
const MANIFEST_PUBLIC_KEYS = [
  'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEUM5exrePPsIdkWFL6IjKdYmEDoEHBZkoBvrApQpmDEhQ7IisLTCiP0byqN+5B5V60QjAj4I/Bw292h8gPGZyOg==', // SP_KEY_1
  'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAERA0j68aAYtwMDagEx2FY+CBbm2+MAYviARSMxWHt1Qt8wGyVvLJ2FqIvg4m2pKfb7GqUwzuJRD/gaOrO2ZJulQ==', // SP_KEY_2
  'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEp7v1KpNTWcI9yJSoZYGvRxsPtciT99P2YDpISVLyD6BDD8xqJ11A8v2/elOEPaSxx5hConszht1cOlPp9YdTsA==', // SP_KEY_DEV
] as const;

async function importEcdsaKey(keyB64: string): Promise<CryptoKey> {
  const keyDer = base64.toBytes(keyB64);
  return crypto.subtle.importKey(
    'spki',
    keyDer,
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['verify'],
  );
}

async function verifyManifestSignature(
  manifestText: string,
  signatureB64: string,
): Promise<boolean> {
  const sigRaw = derToRaw(base64.toBytes(signatureB64));
  const data = new TextEncoder().encode(manifestText);

  for (const keyB64 of MANIFEST_PUBLIC_KEYS) {
    try {
      const key = await importEcdsaKey(keyB64);
      const valid = await crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-256' },
        key,
        sigRaw,
        data,
      );
      if (valid) {
        return true;
      }
    } catch {
      // Key import or verification failed — try next key
    }
  }

  return false;
}

const LOAD_ATTEMPTS = 3;

// The bundle and CEL load at low priority, so the page and the checkout's own
// iframes, served from the same CDN, go first.
const LOW_PRIORITY = { fetchPriority: 'low' } as const;

// A failed download is tried again after a short backoff; one that failed
// verification is not, since the same bytes would come back.
async function withRetries<T>(load: () => Promise<T>): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await load();
    } catch (error) {
      if (error instanceof VerificationError || attempt === LOAD_ATTEMPTS) {
        throw error;
      }
      await delay(300 * attempt);
    }
  }
}

function fetchManifest(
  config: StateProcessorConfig,
): Promise<StateProcessorManifest> {
  return withRetries(async () => {
    const res = await fetch(config.manifestUrl);
    if (!res.ok) {
      throw new Error(
        `Failed to fetch state processor manifest: ${res.status}`,
      );
    }

    const json: SignedManifestResponse = await res.json();
    const { signature, manifest } = json;
    if (typeof signature !== 'string' || typeof manifest !== 'string') {
      throw new Error('Invalid manifest response format');
    }

    const manifestText = base64.decode(manifest);
    if (!(await verifyManifestSignature(manifestText, signature))) {
      throw new VerificationError('Manifest signature verification failed');
    }

    return JSON.parse(manifestText) as StateProcessorManifest;
  });
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

const WASM_BINDGEN_KEY = 'primer_wasm_bindgen';

/**
 * Computes SHA-256 fingerprints (lowercase hex) of each compiled-in manifest public key.
 * The result can be included in telemetry so the backend knows which key set a given
 * SDK build trusts — useful for verifying key rotation adoption across the fleet.
 *
 * Returns one fingerprint per key, in the same order as MANIFEST_PUBLIC_KEYS:
 *   [0] SP_KEY_1
 *   [1] SP_KEY_2
 */
export async function computeManifestKeyFingerprints(): Promise<string[]> {
  return Promise.all(
    MANIFEST_PUBLIC_KEYS.map(async (keyB64) => {
      const keyDer = base64.toBytes(keyB64);
      const hashBuffer = await crypto.subtle.digest('SHA-256', keyDer);
      return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }),
  );
}

/** The environment's manifest; outside production `?manifestUrl=<url>` overrides it for E2E tests. */
export function stateProcessorConfigFor(env: string): StateProcessorConfig {
  const override =
    env !== 'PRODUCTION' && typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('manifestUrl')
      : null;
  return {
    manifestUrl:
      override ??
      `https://sdk.primer.io/state-processor/v0/manifests/${env.toLowerCase()}.json`,
  };
}

// Loaded once per page; a failed load is dropped so the next caller retries.
const manifests = new Map<string, Promise<StateProcessorManifest>>();
const modules = new Map<string, Promise<StateProcessorFactory>>();
const celRuntimes = new Map<string, Promise<void>>();

function once<T>(
  cache: Map<string, Promise<T>>,
  key: string,
  label: string,
  load: () => Promise<T>,
): Promise<T> {
  let pending = cache.get(key);
  if (pending) {
    PrimerJSLogger.debug(`BDC: reusing ${label}`, key);
    return pending;
  }
  const startedAt = Date.now();
  PrimerJSLogger.debug(`BDC: loading ${label}`, key);
  pending = load();
  cache.set(key, pending);
  pending.then(
    () =>
      PrimerJSLogger.info(`BDC: ${label} ready in ${Date.now() - startedAt}ms`),
    (error: unknown) => {
      cache.delete(key);
      PrimerJSLogger.debug(
        `BDC: ${label} failed after ${Date.now() - startedAt}ms`,
        error,
      );
    },
  );
  return pending;
}

function manifestFor(
  config: StateProcessorConfig,
): Promise<StateProcessorManifest> {
  return once(manifests, config.manifestUrl, 'manifest', async () => {
    const manifest = await fetchManifest(config);
    PrimerJSLogger.info('BDC: manifest verified', {
      stateProcessor: manifest.stateProcessor.version,
      cel: manifest.cel.version,
      generatedAt: manifest.generatedAt,
    });
    return manifest;
  });
}

/** The state processor bundle, with the CEL runtime every expression it evaluates runs in. */
export function loadStateProcessorModule(
  config: StateProcessorConfig,
): Promise<StateProcessorFactory> {
  return once(
    modules,
    config.manifestUrl,
    'state processor and CEL',
    async () => {
      const manifest = await manifestFor(config);
      const alreadyOnPage =
        typeof window.StateProcessor?.createStateProcessor === 'function';
      // Scripts load from their Brotli copies. The browser decodes the response
      // before the SRI check, so the sha256 of the uncompressed file applies.
      await Promise.all([
        alreadyOnPage
          ? undefined
          : withRetries(() =>
              loadScriptWithSRI(
                manifest.stateProcessor.umd.br,
                manifest.stateProcessor.umd.sha256,
                LOW_PRIORITY,
              ),
            ),
        loadCel(manifest),
      ]);
      const stateProcessorModule = window.StateProcessor;
      if (!stateProcessorModule?.createStateProcessor) {
        throw new Error(
          'createStateProcessor not found after loading state processor module',
        );
      }
      PrimerJSLogger.info('BDC: state processor bundle on the page', {
        version: manifest.stateProcessor.version,
        alreadyOnPage,
      });
      return stateProcessorModule;
    },
  );
}

function loadCel(manifest: StateProcessorManifest): Promise<void> {
  const celTarget = manifest.cel.noModules;
  return once(celRuntimes, celTarget.wasm.url, 'CEL runtime', async () => {
    const [wasmBytes] = await Promise.all([
      withRetries(() =>
        fetchAndVerify(celTarget.wasm.br, celTarget.wasm.sha256, {
          priority: 'low',
        }),
      ),
      typeof window[WASM_BINDGEN_KEY] === 'function'
        ? undefined
        : withRetries(() =>
            loadScriptWithSRI(
              celTarget.js.br,
              celTarget.js.sha256,
              LOW_PRIORITY,
            ),
          ),
    ]);

    const wasmBindgen = window[WASM_BINDGEN_KEY];
    if (typeof wasmBindgen !== 'function') {
      throw new Error(
        `Invalid CEL wrapper: window.${WASM_BINDGEN_KEY} not set after script load`,
      );
    }

    // The genuine hardened CEL wrapper locks its global slot (non-writable,
    // non-configurable) and deep-freezes the API immediately after assigning it.
    const slot = Object.getOwnPropertyDescriptor(window, WASM_BINDGEN_KEY);
    if (!slot || slot.configurable !== false || slot.writable !== false) {
      throw new Error(
        `Invalid CEL wrapper: window.${WASM_BINDGEN_KEY} global is not hardened`,
      );
    }

    await wasmBindgen(wasmBytes);
  });
}

export async function loadStateProcessor(
  config: StateProcessorConfig,
  schema: SerializedJSON,
  sdkContext: SDKContext,
): Promise<StateProcessor> {
  const [manifest, stateProcessorModule] = await Promise.all([
    manifestFor(config),
    loadStateProcessorModule(config),
  ]);
  PrimerJSLogger.info('BDC: creating state processor for the flow', {
    stateProcessor: manifest.stateProcessor.version,
  });
  return stateProcessorModule.createStateProcessor(schema, sdkContext);
}
